import { IdentifierKeyMap, TIdentifierObject, TIdentifierValue } from "@/constants/Test";

export function accessible() {
  return true;
}
export function controllable() {
  return true;
}

interface TTreeProps {
  key: string;
  children?: TTreeProps[];
  [key: string]: any;
}

export const getLevelKeys = (tree: TTreeProps[]) => {
  const key: Record<string, number> = {};
  const func = (_: TTreeProps[], level = 1) => {
    _.forEach(item => {
      if (item.key) {
        key[item.key] = level;
      }
      if (item.children) {
        func(item.children, level + 1);
      }
    });
  };
  func(tree);
  return key;
};

export function getTreeLeaf(tree: TTreeProps[], key: string, value: string, parentKey: TTreeProps[] = []): { leaf: TTreeProps; parentKey: TTreeProps[] } | null {
  const result = tree.find(item => item[key].replace(/\/$/, "") === value.replace(/\/$/, ""));
  if (result) {
    return {
      leaf: result,
      parentKey: parentKey,
    };
  }
  for (const item of tree) {
    if (item.children) {
      const childResult = getTreeLeaf(item.children, key, value, parentKey.concat(item));
      if (childResult) {
        return childResult;
      }
    }
  }
  return null;
}

export function capitalizeFirstLetter(val: string) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

/**
 * Picks identifier properties from an object based on the provided keys.
 * This function only extracts - it does NOT validate.
 * Use `hasRequiredIdentifiers` for validation.
 *
 * @param source - Source object containing identifier properties
 * @param keys - Keys to pick from IdentifierKeyMap. If empty array, extracts all found identifier keys.
 * @returns Object with only the specified identifier properties (may be partial)
 *
 * @example
 * // Extract specific identifiers
 * pickIdentifiers(data, [IdentifierKeyMap.PROJECT, IdentifierKeyMap.BUILD])
 * // Returns: { projectId: "...", buildId: "..." } (only if they exist in source)
 *
 * @example
 * // Extract all found identifiers (empty array = extract all)
 * pickIdentifiers(data, [])
 * // Returns all identifier properties found in data
 *
 * @example
 * // Default behavior: extracts PROJECT, BUILD, PHASE if present
 * pickIdentifiers(data)
 */
export function pickIdentifiers(source: Record<string, any> | undefined | null, keys: TIdentifierValue[] = [IdentifierKeyMap.PROJECT, IdentifierKeyMap.BUILD, IdentifierKeyMap.PHASE]): Partial<TIdentifierObject> {
  if (!source) {
    return {};
  }

  const result: Record<string, string> = {};

  // If empty keys array, extract all found identifier keys in source
  if (keys.length === 0) {
    Object.entries(IdentifierKeyMap).forEach(([, mappedValue]) => {
      if (mappedValue && source[mappedValue] !== undefined && source[mappedValue] !== null && source[mappedValue] !== "") {
        result[mappedValue] = source[mappedValue];
      }
    });
    return result;
  }

  // Extract only specified keys (if they exist and have valid values)
  keys.forEach(key => {
    if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
      result[key] = source[key];
    }
  });

  return result;
}

/**
 * Type guard that validates an identifier object has all required keys with non-empty values.
 * Use this to safely narrow the type before API calls.
 *
 * @param obj - The identifier object to validate
 * @param requiredKeys - Keys that must be present with non-empty values
 * @returns True if all required keys are present and valid, false otherwise
 *
 * @example
 * const identifiers = pickIdentifiers(drawer);
 * if (hasRequiredIdentifiers(identifiers, [IdentifierKeyMap.PROJECT, IdentifierKeyMap.BUILD])) {
 *   // TypeScript now knows identifiers has PROJECT and BUILD
 *   fetch_Something(identifiers);
 * }
 */
export function hasRequiredIdentifiers<K extends TIdentifierValue>(obj: Partial<TIdentifierObject> | undefined | null, requiredKeys: K[]): obj is Record<K, string> & Partial<TIdentifierObject> {
  if (!obj) return false;
  return requiredKeys.every(key => obj[key] !== undefined && obj[key] !== null && obj[key] !== "");
}

/**
 * Combined helper: extracts identifiers and validates required keys.
 * Returns the validated object or undefined if validation fails.
 *
 * @param source - Source object to extract identifiers from
 * @param requiredKeys - Keys that must be present for validation to pass (default: [PROJECT, PHASE, BUILD])
 * @returns Validated identifier object or undefined
 *
 * @example
 * // Use default keys (PROJECT, PHASE, BUILD)
 * const identifiers = getValidatedIdentifiers(drawer);
 * if (identifiers) {
 *   fetch_Something(identifiers);
 * }
 *
 * @example
 * // Specify custom required keys
 * const identifiers = getValidatedIdentifiers(drawer, [PROJECT, BUILD, PHASE, TEST]);
 * if (identifiers) {
 *   fetch_Something(identifiers);
 * }
 */
export function getValidatedIdentifiers<K extends TIdentifierValue>(
  source: Record<string, any> | undefined | null,
  requiredKeys: K[] = [IdentifierKeyMap.PROJECT, IdentifierKeyMap.TEST_LOCATION, IdentifierKeyMap.PHASE, IdentifierKeyMap.BUILD] as K[],
): (Record<K, string> & Partial<TIdentifierObject>) | undefined {
  const extracted = pickIdentifiers(source, requiredKeys);
  if (hasRequiredIdentifiers(extracted, requiredKeys)) {
    return extracted;
  }
  return undefined;
}
